// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/autocomplete.js
import { createPopper } from "@popperjs/core"
import BaseComponent from "./base_component"
import { parseList } from "./dom"
import { setTemplate } from "./template"

let generatedId = 0

export default class Autocomplete extends BaseComponent {
  static componentName = "autocomplete"
  static defaults = { options: [], cleaner: true, indicator: true, search: "global", allowOnlyDefinedOptions: false }

  constructor(element, config = {}) {
    super(element, config)
    this.setOptions(this.config.options)
    element.classList.add("autocomplete")
    if (this.config.disabled) element.classList.add("disabled")
    const group = document.createElement("div")
    group.className = "autocomplete-input-group"
    if (this.config.showHints) {
      this.hint = document.createElement("input")
      this.hint.className = "autocomplete-input autocomplete-input-hint"
      this.hint.tabIndex = -1
      this.hint.readOnly = true
      this.hint.setAttribute("aria-hidden", "true")
      group.append(this.hint)
    }
    this.input = document.createElement("input")
    this.input.className = "autocomplete-input"
    this.input.id = this.config.id || `autocomplete-${++generatedId}`
    if (this.config.name) this.input.name = this.config.name
    this.input.placeholder = this.config.placeholder || "Select..."
    this.input.autocomplete = "off"
    this.input.disabled = Boolean(this.config.disabled)
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-autocomplete", this.hint ? "both" : "list")
    this.input.setAttribute("aria-expanded", "false")
    group.append(this.input)
    const buttons = document.createElement("div")
    buttons.className = "autocomplete-buttons"
    for (const [name, label, action] of [
      ["cleaner", "Clear selection", () => this.clear()],
      ["indicator", "Toggle suggestions", () => (this.element.classList.contains("show") ? this.hide() : this.show())],
    ]) {
      if (!this.config[name]) continue
      const button = document.createElement("button")
      button.type = "button"
      button.className = `autocomplete-${name}`
      button.setAttribute("aria-label", label)
      button.disabled = Boolean(this.config.disabled)
      this.listen(button, "click", action)
      buttons.append(button)
    }
    group.append(buttons)
    this.menu = document.createElement("div")
    this.menu.className = "autocomplete-dropdown"
    this.list = document.createElement("div")
    this.list.className = "autocomplete-options"
    this.list.id = `${this.input.id}-options`
    this.list.setAttribute("role", "listbox")
    this.input.setAttribute("aria-controls", this.list.id)
    this.menu.append(this.list)
    element.replaceChildren(group, this.menu)
    this.listen(this.input, "input", () => {
      this.syncSelection()
      this.renderOptions()
      this.emit("search", { value: this.input.value })
      this.show()
    })
    this.listen(this.input, "focus", () => this.show())
    this.listen(this.input, "blur", () => {
      if (this.input.value && this.config.allowOnlyDefinedOptions && !this.selected) this.clear()
    })
    this.listen(element, "focusout", (event) => {
      if (!element.contains(event.relatedTarget)) this.hide()
    })
    this.listen(this.input, "keydown", (event) => this.keydown(event))
    this.listen(this.list, "mousedown", (event) => event.preventDefault())
    this.listen(this.list, "click", (event) => {
      const option = event.target.closest("[data-option-index]")
      if (option) this.choose(this.matches[Number(option.dataset.optionIndex)])
    })
    this.listen(document, "click", (event) => {
      if (!element.contains(event.target)) this.hide()
    })
    this.selected =
      this.options.find(
        (option) => option.selected || (this.config.value != null && option.value === String(this.config.value)),
      ) || null
    this.input.value = this.selected?.label || ""
    this.renderOptions()
  }

  setOptions(options) {
    const flatten = (items, group = null, disabled = false) =>
      parseList(items).flatMap((item) => {
        const option = typeof item === "string" ? { label: item, value: item } : item
        if (option.options) return flatten(option.options, option.label, disabled || Boolean(option.disabled))
        return {
          ...option,
          label: String(option.label ?? option.value),
          value: String(option.value ?? option.label),
          group,
          groupDisabled: disabled,
          disabled: disabled || Boolean(option.disabled),
        }
      })
    this.options = flatten(options)
  }

  update(config = {}) {
    this.config = { ...this.config, ...config }
    if (Object.prototype.hasOwnProperty.call(config, "options")) {
      this.setOptions(config.options)
      this.syncSelection(true)
    }
    if (this.config.name) this.input.name = this.config.name
    else this.input.removeAttribute("name")
    this.renderOptions()
    this.popper?.update()
  }

  syncSelection(preserveSelection = false) {
    const previous = this.selected
    // Remote search results are only a page of suggestions, not the full set of valid choices.
    const selected = preserveSelection && parseList(this.config.search).includes("external") ? this.selected : null
    this.selected =
      this.options.find(
        (option) => !option.disabled && option.label.toLowerCase() === this.input.value.toLowerCase(),
      ) ||
      (selected &&
      !selected.disabled &&
      selected.label.toLowerCase() === this.input.value.toLowerCase() &&
      !this.options.some((option) => option.value === selected.value)
        ? selected
        : null)
    if (!preserveSelection && previous?.value !== this.selected?.value) this.emit("changed", { value: this.selected })
  }

  renderOptions() {
    const query = this.input.value.trim().toLowerCase()
    this.matches = this.options.filter(
      (option) => parseList(this.config.search).includes("external") || option.label.toLowerCase().includes(query),
    )
    this.active = -1
    this.input.removeAttribute("aria-activedescendant")
    this.list.replaceChildren()
    let group
    this.matches.forEach((option, index) => {
      if (option.group && option.group !== group) {
        group = option.group
        const label = document.createElement("div")
        label.className = `autocomplete-optgroup-label${option.groupDisabled ? " disabled" : ""}`
        label.textContent = group
        this.list.append(label)
      }
      const item = document.createElement("div")
      item.className = `autocomplete-option${option.disabled ? " disabled" : ""}`
      item.setAttribute("aria-disabled", Boolean(option.disabled))
      item.id = `${this.input.id}-option-${index}`
      item.dataset.optionIndex = index
      item.setAttribute("role", "option")
      item.setAttribute("aria-selected", option === this.selected)
      if (this.config.optionsTemplate) setTemplate(item, this.config.optionsTemplate(option))
      else if (query && this.config.highlightOptionsOnSearch) {
        const start = option.label.toLowerCase().indexOf(query)
        if (start < 0) {
          item.textContent = option.label
          this.list.append(item)
          return
        }
        const mark = document.createElement("mark")
        mark.textContent = option.label.slice(start, start + query.length)
        item.append(option.label.slice(0, start), mark, option.label.slice(start + query.length))
      } else item.textContent = option.label
      this.list.append(item)
    })
    if (!this.matches.length && this.config.searchNoResultsLabel !== false) {
      const empty = document.createElement("div")
      empty.className = "autocomplete-options-empty"
      empty.setAttribute("role", "status")
      empty.textContent = this.config.searchNoResultsLabel || "No results found"
      this.list.append(empty)
    }
    this.hintOption = query
      ? this.matches.find((option) => !option.disabled && option.label.toLowerCase().startsWith(query))
      : null
    if (this.hint)
      this.hint.value = this.hintOption ? this.input.value + this.hintOption.label.slice(this.input.value.length) : ""
  }

  keydown(event) {
    if (event.key === "Escape") return this.hide()
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault()
      this.show()
      const enabled = this.matches.map((option, index) => (option.disabled ? -1 : index)).filter((index) => index >= 0)
      const current = enabled.indexOf(this.active)
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? enabled.length - 1
            : current === -1
              ? event.key === "ArrowDown"
                ? 0
                : enabled.length - 1
              : (current + (event.key === "ArrowDown" ? 1 : -1) + enabled.length) % enabled.length
      this.active = enabled[next] ?? -1
      this.list
        .querySelectorAll("[data-option-index]")
        .forEach((item, index) => item.classList.toggle("focused", index === this.active))
      const item = this.list.querySelector(`[data-option-index="${this.active}"]`)
      if (item) {
        this.input.setAttribute("aria-activedescendant", item.id)
        item.scrollIntoView({ block: "nearest" })
      }
    }
    if (event.key === "Enter" && this.element.classList.contains("show") && this.active >= 0) {
      event.preventDefault()
      this.choose(this.matches[this.active])
    }
    if (event.key === "ArrowRight" && this.hint?.value) {
      event.preventDefault()
      this.choose(this.hintOption)
    }
  }

  choose(option, focus = true) {
    if (option?.disabled) return
    this.selected = option
    this.input.value = option?.label || ""
    this.renderOptions()
    this.emit("changed", { value: option })
    if (focus) this.input.focus()
    this.hide()
  }

  clear() {
    this.selected = null
    this.input.value = ""
    this.renderOptions()
    this.emit("search", { value: "" })
    this.emit("changed", { value: null })
  }

  show() {
    if (
      this.config.disabled ||
      this.element.classList.contains("show") ||
      (!this.matches.length && this.config.searchNoResultsLabel === false)
    )
      return
    this.element.classList.add("show")
    this.menu.classList.add("show")
    this.input.setAttribute("aria-expanded", "true")
    this.popper = createPopper(this.input.parentElement, this.menu, { placement: "bottom-start" })
  }

  hide() {
    this.active = -1
    this.list.querySelectorAll(".focused").forEach((item) => item.classList.remove("focused"))
    this.element.classList.remove("show")
    this.menu.classList.remove("show")
    this.input.setAttribute("aria-expanded", "false")
    this.input.removeAttribute("aria-activedescendant")
    this.popper?.destroy()
    this.popper = null
  }

  dispose() {
    if (this.signal.aborted) return
    this.hide()
    this.element.innerHTML = this.originalHTML
    this.element.className = this.originalClassName
    super.dispose()
  }
  snapshotState() {
    return {
      config: { options: this.config.options, value: this.selected?.value ?? null },
      selected: this.selected,
      text: this.input.value,
    }
  }
  restoreState(state) {
    if (state.text !== undefined) {
      this.input.value = state.text
      this.selected = state.selected || this.selected
      this.syncSelection(true)
      this.renderOptions()
    }
  }
}
